import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const tiers = [
  {
    name: 'Problem Awareness',
    subtitle: 'For the Newly Concerned',
    price: '2,500',
    period: 'per month',
    description: 'Begin your journey into comprehensive problem over-identification. Ideal for those just discovering how complicated their life can become.',
    features: [
      'Up to 50 problems identified & over-complicated monthly',
      'Basic escalation protocols',
      'Email problem notifications',
      'Monthly over-analysis reports',
      'Standard documentation access',
      'Community forum access',
      '3 complimentary bonus problems per month'
    ],
    cta: 'Start Awareness',
    popular: false
  },
  {
    name: 'Problem Mastery',
    subtitle: 'For the Truly Committed',
    price: '7,500',
    period: 'per month',
    description: 'Our most popular program for those serious about never running out of problems. Unlimited over-thinking, maximum over-complication.',
    features: [
      'Unlimited problem identification & over-complication',
      'Priority escalation pathways',
      'Real-time problem alerts (SMS + Email)',
      'Weekly over-thinking consultations',
      'Advanced documentation suite',
      'Dedicated Over-Complication Coordinator',
      'VIP workshop access',
      'Quarterly bonus problem summits',
      'Guaranteed 7+ bonus problems per month'
    ],
    cta: 'Achieve Mastery',
    popular: true
  },
  {
    name: 'Problem Enlightenment',
    subtitle: 'For the Elite Few',
    price: '25,000',
    period: 'per month',
    description: 'The ultimate over-complication experience. For those who demand that their problems be as elaborate, recursive, and inexhaustible as possible.',
    features: [
      'Everything in Problem Mastery',
      'White-glove over-complication concierge',
      'Daily one-on-one over-thinking sessions',
      'Custom 14-framework analysis per issue',
      'Blockchain bonus problem verification',
      'Private problem retreat access',
      'Executive over-analysis briefings',
      'Multi-generational bonus problem planning',
      'Dr. Thornwell personal consultation',
      'Unlimited complimentary bonus problems'
    ],
    cta: 'Reach Enlightenment',
    popular: false
  }
];

const PricingSection = () => {
  return (
    <section id="pricing" className="relative py-32 bg-gradient-to-b from-black via-zinc-950 to-black">
      {/* Background Image Overlay */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://public.readdy.ai/ai/img_res/39cfefd25b3983146950b3a59689c91d.jpg"
          alt="Mountain range"
          width={1920}
          height={1080}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-3 mb-6"
          >
            <div className="w-12 h-[2px] bg-gold"></div>
            <span className="text-gold text-sm font-semibold tracking-widest uppercase">
              Investment Tiers
            </span>
            <div className="w-12 h-[2px] bg-gold"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl font-black text-white mb-6"
          >
            Choose Your <span className="text-gold">Level of Commitment</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-cream/70 max-w-3xl mx-auto"
          >
            Each tier represents a deeper commitment to over-thinking, over-complicating, and ensuring 
            your problems never run out. All plans include complimentary bonus problems at no extra charge.
          </motion.p>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {tiers.map((tier, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative bg-gradient-to-br ${
                tier.popular
                  ? 'from-gold/10 via-zinc-900 to-black border-2 border-gold'
                  : 'from-zinc-900 to-black border border-gold/20'
              } rounded-2xl p-8 hover:border-gold/50 transition-all duration-500 ${
                tier.popular ? 'lg:scale-105 lg:shadow-2xl lg:shadow-gold/20' : ''
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gold text-black px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap">
                  Most Popular
                </div>
              )}

              {/* Header */}
              <div className="text-center mb-8 pt-4">
                <h3 className="text-2xl font-bold text-white mb-2">{tier.name}</h3>
                <p className="text-gold text-sm font-semibold mb-6">{tier.subtitle}</p>
                <div className="flex items-baseline justify-center gap-2 mb-4">
                  <span className="text-5xl font-black text-white">${tier.price}</span>
                  <span className="text-cream/60 text-sm">{tier.period}</span>
                </div>
                <p className="text-cream/70 text-sm leading-relaxed">{tier.description}</p>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-cream/80">
                    <i className="ri-checkbox-circle-fill text-gold mt-1 flex-shrink-0"></i>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                to="/contact"
                className={`block text-center px-8 py-4 rounded-full text-base font-bold transition-all duration-300 whitespace-nowrap ${
                  tier.popular
                    ? 'bg-gold text-black hover:bg-white'
                    : 'bg-white/10 text-white hover:bg-gold hover:text-black border border-gold/30'
                }`}
              >
                {tier.cta}
              </Link>

              {/* Decorative Element */}
              <div className="absolute bottom-0 right-0 w-32 h-32 bg-gold/5 rounded-full blur-3xl"></div>
            </motion.div>
          ))}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-zinc-900/50 border border-gold/20 rounded-2xl p-8 max-w-4xl mx-auto">
            <h4 className="text-2xl font-bold text-white mb-4">Enterprise Over-Complication Solutions</h4>
            <p className="text-cream/70 mb-6">
              For organizations requiring comprehensive problem multiplication across multiple departments, 
              we offer bespoke enterprise solutions starting at $100,000 per month. Our over-educated team 
              will apply their wide spectrum of expertise to ensure your entire organization never runs out of problems.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 text-gold hover:text-white transition-colors duration-300 font-semibold"
            >
              Discuss Enterprise Needs
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;